import { t } from "@gladis/ui/i18n";
import { RECURRENCE_INTERVALS, type RecurrenceIntervalValue } from "../../index.js";

/**
 * The six intervals as the interface needs them: a label to show, and the ratio the form's preview uses.
 *
 * ONE PLACE, for the same reason the category catalogue is one place — and this file exists because the
 * first draft had the labels in `ExpenseList` and the labels plus ratios in `ExpenseForm`, which is two
 * copies of one list. The category architecture test would not have caught it, since it only knows about
 * categories, so the duplication would have drifted quietly: a member would eventually read "Every 4 weeks"
 * on one screen and "Every four weeks" on the other.
 *
 * THE RATIOS HERE ARE FOR THE PREVIEW ONLY. The authoritative ones live on `RecurrenceInterval` in the
 * domain as `bigint` pairs, and every stored or displayed figure goes through `Valuation.scaledBy`. These
 * are numbers because the preview is a hint that is thrown away on submit — see `ExpenseForm`'s note.
 *
 * `RECURRENCE_INTERVALS` from the domain is the source of the SET; the assertion below is what keeps the
 * two from drifting apart if a seventh interval is ever added.
 */

export interface IntervalOption {
  readonly value: RecurrenceIntervalValue;
  /**
   * THE KEY, NOT THE WORD — feature 021.
   *
   * This held an English label evaluated at module scope, which freezes it in whichever language
   * loaded first and is invisible to a sweep of `.tsx` files. The six words live in
   * `@gladis/ui/i18n` now, shared with income's copy of this same list.
   */
  readonly labelKey: string;
  /** Preview only. See the header. */
  readonly numerator: number;
  readonly denominator: number;
}

export const INTERVAL_OPTIONS: readonly IntervalOption[] = [
  { value: "weekly", labelKey: "interval.weekly", numerator: 52, denominator: 12 },
  { value: "every_4_weeks", labelKey: "interval.every_4_weeks", numerator: 13, denominator: 12 },
  { value: "monthly", labelKey: "interval.monthly", numerator: 1, denominator: 1 },
  { value: "quarterly", labelKey: "interval.quarterly", numerator: 1, denominator: 3 },
  { value: "half_yearly", labelKey: "interval.half_yearly", numerator: 1, denominator: 6 },
  { value: "yearly", labelKey: "interval.yearly", numerator: 1, denominator: 12 },
];

/**
 * Fails at import time if the domain gains an interval this list has not been given a label for.
 *
 * A `Record<RecurrenceIntervalValue, …>` would have caught it at compile time and would not have allowed the
 * ordering, which is display order. This is the next best thing and it is loud.
 */
if (INTERVAL_OPTIONS.length !== RECURRENCE_INTERVALS.length) {
  throw new Error(
    `INTERVAL_OPTIONS has ${String(INTERVAL_OPTIONS.length)} entries but the domain declares ` +
      `${String(RECURRENCE_INTERVALS.length)} intervals. Give the new one a label.`,
  );
}

export function intervalLabel(value: string): string {
  const option = INTERVAL_OPTIONS.find((entry) => entry.value === value);
  return option === undefined ? value : t(option.labelKey);
}
